import { useState } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ProgressRing, WeeklyProgress, MultiProgressRing } from "@/components/apex/ProgressRing"
import { AnimatedCard } from "@/components/apex/AnimatedCard"
import { useApexState } from "@/hooks/useApexState"
import { Plus, Activity, Target, Calendar, TrendingUp } from "lucide-react"

const weeklyGoal = {
  target: 5,
  current: 3,
  label: "movement sessions"
}

const recentActivities = [
  {
    id: "1",
    type: "Run",
    feeling: "Energetic",
    duration: "30 min",
    date: "Today",
    points: 25,
    notes: "Morning run in Central Park"
  },
  {
    id: "2", 
    type: "Train",
    feeling: "Strong",
    duration: "45 min",
    date: "Yesterday",
    points: 30,
    notes: "Full body strength session"
  },
  {
    id: "3",
    type: "Walk",
    feeling: "Peaceful",
    duration: "20 min",
    date: "2 days ago",
    points: 15,
    notes: "Evening walk with friends"
  }
]

const movementTypes = [
  { id: "run", label: "Run", icon: "🏃‍♂️", points: 25 },
  { id: "walk", label: "Walk", icon: "🚶‍♂️", points: 15 },
  { id: "train", label: "Train", icon: "💪", points: 30 },
  { id: "cycle", label: "Cycle", icon: "🚴‍♂️", points: 20 },
  { id: "yoga", label: "Yoga", icon: "🧘‍♀️", points: 20 },
  { id: "other", label: "Other", icon: "✨", points: 20 }
]

const feelings = [
  { id: "energetic", label: "Energetic", emoji: "⚡" },
  { id: "strong", label: "Strong", emoji: "💪" },
  { id: "peaceful", label: "Peaceful", emoji: "🧘‍♀️" },
  { id: "accomplished", label: "Accomplished", emoji: "🎯" },
  { id: "refreshed", label: "Refreshed", emoji: "✨" },
  { id: "focused", label: "Focused", emoji: "🎯" }
]

const weeklyStats = {
  totalSessions: 12,
  totalPoints: 280,
  streak: 5,
  favoriteActivity: "Run"
}

const weeklyData = [
  { day: 'Monday', completed: true, intensity: 'high' as const },
  { day: 'Tuesday', completed: true, intensity: 'medium' as const },
  { day: 'Wednesday', completed: false },
  { day: 'Thursday', completed: true, intensity: 'high' as const },
  { day: 'Friday', completed: false },
  { day: 'Saturday', completed: true, intensity: 'low' as const },
  { day: 'Sunday', completed: false }
]

const monthlyGoals = [
  { label: 'Movement', progress: 65, color: 'accent' as const },
  { label: 'Consistency', progress: 80, color: 'primary' as const },
  { label: 'Challenges', progress: 40, color: 'secondary' as const }
]
export function ApexMove() {
  const { state } = useApexState()
  const [showQuickAdd, setShowQuickAdd] = useState(false)
  const [selectedActivity, setSelectedActivity] = useState("")
  const [selectedFeeling, setSelectedFeeling] = useState("")
  const [duration, setDuration] = useState("")

  const weeklyProgress = (weeklyGoal.current / weeklyGoal.target) * 100

  const handleQuickAdd = () => {
    if (selectedActivity && selectedFeeling) {
      // Add logic to save activity
      console.log("Adding activity:", { selectedActivity, selectedFeeling, duration })
      setShowQuickAdd(false)
      setSelectedActivity("")
      setSelectedFeeling("")
      setDuration("")
    }
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-lg">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-foreground">Move</h1>
            <Button 
              size="sm" 
              className="bg-accent hover:bg-accent/90 text-accent-foreground"
              onClick={() => setShowQuickAdd(!showQuickAdd)}
            >
              <Plus size={16} className="mr-1" />
              Log Movement
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Quick Add Movement */}
        {showQuickAdd && (
          <AnimatedCard>
            <Card className="bg-card-gradient border-0 animate-scale-in">
              <CardHeader>
                <h3 className="text-lg font-semibold">Log Your Movement</h3>
                <p className="text-sm text-muted-foreground">
                  Track your movement and earn Apex Points
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Activity Type */}
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    What did you do?
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {movementTypes.map(type => (
                      <button
                        key={type.id}
                        onClick={() => setSelectedActivity(type.id)}
                        className={`
                          p-3 rounded-lg text-center transition-all duration-200
                          ${selectedActivity === type.id 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted hover:bg-muted/80'
                          }
                        `}
                      >
                        <div className="text-lg mb-1">{type.icon}</div>
                        <div className="text-xs font-medium">{type.label}</div>
                        <div className="text-xs opacity-70">+{type.points}pts</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* How do you feel */}
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    How do you feel?
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {feelings.map(feeling => (
                      <button
                        key={feeling.id}
                        onClick={() => setSelectedFeeling(feeling.id)}
                        className={`
                          p-2 rounded-lg text-left transition-all duration-200
                          ${selectedFeeling === feeling.id 
                            ? 'bg-accent text-accent-foreground' 
                            : 'bg-muted hover:bg-muted/80'
                          }
                        `}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{feeling.emoji}</span>
                          <span className="text-sm font-medium">{feeling.label}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Duration (optional) */}
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Duration (optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 30 min"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-input border border-border text-foreground"
                  />
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowQuickAdd(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground"
                    onClick={handleQuickAdd}
                    disabled={!selectedActivity || !selectedFeeling}
                  >
                    Save Movement
                  </Button>
                </div>
              </CardContent>
            </Card>
          </AnimatedCard>
        )}

        {/* Weekly Progress */}
        <AnimatedCard hoverScale>
          <Card className="bg-card-gradient border-0">
            <CardContent className="p-6">
              <WeeklyProgress days={weeklyData} />
            </CardContent>
          </Card>
        </AnimatedCard>

        {/* Monthly Goals Overview */}
        <AnimatedCard hoverScale>
          <Card className="bg-card-gradient border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-foreground">Monthly Goals</h3>
                <Button variant="ghost" size="sm" className="text-primary">
                  <Target size={16} className="mr-1" />
                  Adjust
                </Button>
              </div>

              <div className="flex items-center justify-center mb-6">
                <MultiProgressRing
                  size="lg"
                  rings={monthlyGoals}
                >
                  <div className="text-center">
                    <div className="text-2xl font-bold text-foreground">
                      {Math.round(monthlyGoals.reduce((avg, goal) => avg + goal.progress, 0) / monthlyGoals.length)}%
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Average
                    </div>
                  </div>
                </MultiProgressRing>
              </div>

              <div className="grid grid-cols-3 gap-4 text-center">
                {monthlyGoals.map((goal, index) => (
                  <div key={index} className="space-y-2">
                    <ProgressRing
                      progress={goal.progress}
                      size="sm"
                      color={goal.color}
                      className="mx-auto"
                    >
                      <span className="text-xs font-bold">
                        {goal.progress}%
                      </span>
                    </ProgressRing>
                    <p className="text-xs text-muted-foreground font-medium">
                      {goal.label}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </AnimatedCard>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 gap-4">
          <AnimatedCard hoverScale>
            <Card className="bg-card-gradient border-0">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-primary mb-1">
                  {weeklyStats.totalSessions}
                </div>
                <div className="text-sm text-muted-foreground">
                  Total Sessions
                </div>
              </CardContent>
            </Card>
          </AnimatedCard>

          <AnimatedCard hoverScale>
            <Card className="bg-card-gradient border-0">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-accent mb-1">
                  {weeklyStats.totalPoints}
                </div>
                <div className="text-sm text-muted-foreground">
                  Points Earned
                </div>
              </CardContent>
            </Card>
          </AnimatedCard>

          <AnimatedCard hoverScale>
            <Card className="bg-card-gradient border-0">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-secondary mb-1">
                  {weeklyStats.streak}
                </div>
                <div className="text-sm text-muted-foreground">
                  Day Streak
                </div>
              </CardContent>
            </Card>
          </AnimatedCard>

          <AnimatedCard hoverScale>
            <Card className="bg-card-gradient border-0">
              <CardContent className="p-4 text-center">
                <div className="text-lg font-bold text-foreground mb-1">
                  🏃‍♂️
                </div>
                <div className="text-sm text-muted-foreground">
                  Favorite: {weeklyStats.favoriteActivity}
                </div>
              </CardContent>
            </Card>
          </AnimatedCard>
        </div>

        {/* Recent Activities */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Recent Movement</h3>
            <Button variant="ghost" size="sm" className="text-primary">
              <TrendingUp size={16} className="mr-1" />
              View All
            </Button>
          </div>
          <div className="space-y-3">
            {recentActivities.map(activity => (
              <AnimatedCard key={activity.id}>
                <Card className="bg-card-gradient border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                          <Activity size={20} className="text-accent" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-foreground">{activity.type}</h4>
                            <Badge variant="secondary" className="text-xs">
                              {activity.feeling}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{activity.duration}</span>
                            <span>•</span>
                            <span>{activity.date}</span>
                          </div>
                          {activity.notes && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {activity.notes}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-accent">
                          +{activity.points}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          points
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}